import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatWhen } from '../inquiries/inquiryShared';

const POLL_MS = 15000;
const TOAST_MS = 15000;
const ICONS = { new_inquiry: '📥', customer_message: '💬', assigned: '👉', escalated: '⏰' };

function chime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch {}
}

function urlBase64ToUint8Array(base64) {
  const padded = (base64 + '='.repeat((4 - base64.length % 4) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(padded), c => c.charCodeAt(0));
}

const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone;
const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Polls for new inquiry notifications; shows pop-ups, plays a chime, and manages push alerts for this device.
export default function NotificationCenter({ onUnreadChange }) {
  const navigate = useNavigate();
  const lastId = useRef(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState('unknown'); // unknown | on | off | unsupported | denied
  const pushOn = useRef(false);

  const go = useCallback((n) => {
    setOpen(false);
    setToasts(t => t.filter(x => x.id !== n.id));
    navigate(`/admin/inquiries?id=${n.inquiry_id}`);
  }, [navigate]);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { after: lastId.current || 0 } });
      setUnread(data.unread);
      if (lastId.current === null) {
        // First load: show history in the bell, but don't pop up old alerts
        setItems(data.items);
        lastId.current = data.items[0]?.id || 0;
        return;
      }
      if (!data.items.length) return;
      lastId.current = data.items[0].id;
      setItems(prev => [...data.items, ...prev].slice(0, 50));
      setToasts(prev => [...data.items.slice(0, 3), ...prev].slice(0, 4));
      chime();
      window.dispatchEvent(new Event('inquiries:changed'));
      // Push already shows a system notification on this device; otherwise show one ourselves when the tab is in the background
      if (!pushOn.current && document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        data.items.slice(0, 3).forEach(n => {
          const note = new Notification(n.title, { body: n.body || '', tag: `inquiry-${n.inquiry_id}`, icon: '/logo192.png' });
          note.onclick = () => { window.focus(); go(n); };
        });
      }
    } catch {}
  }, [go]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [poll]);

  useEffect(() => { onUnreadChange?.(unread); }, [unread, onUnreadChange]);

  // Auto-dismiss pop-ups
  useEffect(() => {
    if (!toasts.length) return;
    const t = setTimeout(() => setToasts(prev => prev.slice(0, -1)), TOAST_MS);
    return () => clearTimeout(t);
  }, [toasts]);

  // Is push already on for this device?
  useEffect(() => {
    if (!pushSupported) { setPushState('unsupported'); return; }
    if (Notification.permission === 'denied') { setPushState('denied'); return; }
    navigator.serviceWorker.getRegistration('/').then(reg => reg?.pushManager.getSubscription()).then(sub => {
      pushOn.current = !!sub;
      setPushState(sub ? 'on' : 'off');
    }).catch(() => setPushState('off'));
  }, []);

  const enableAlerts = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setPushState(permission === 'denied' ? 'denied' : 'off'); return; }
      const { data } = await api.get('/notifications/push-key');
      if (!data.publicKey) { setPushState('on'); return; } // server push not configured: in-tab notifications only
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) });
      await api.post('/notifications/push-subscriptions', sub.toJSON());
      pushOn.current = true;
      setPushState('on');
    } catch (e) {
      console.error('Enable alerts failed', e);
      setPushState('off');
    }
  };

  const disableAlerts = async () => {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.delete('/notifications/push-subscriptions', { data: { endpoint: sub.endpoint } }).catch(() => {});
      await sub.unsubscribe();
    }
    pushOn.current = false;
    setPushState('off');
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || 'now' })));
    setUnread(0);
  };

  return (
    <>
      {/* Bell */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpen(o => !o)} title="Notifications"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, position: 'relative', padding: 4 }}>
          🔔
          {unread > 0 && <span style={{ position: 'absolute', top: -2, right: -4, background: '#c62828', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 800, padding: '1px 5px', minWidth: 16 }}>{unread > 99 ? '99+' : unread}</span>}
        </button>
        {open && (
          <div style={{ position: 'absolute', right: 0, top: 40, width: 340, maxWidth: 'calc(100vw - 32px)', background: '#fff', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', zIndex: 1000, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <strong style={{ color: '#1a2744', fontSize: 14 }}>Notifications</strong>
              {unread > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#c9a227', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Mark all read</button>}
            </div>
            <AlertsSetting state={pushState} onEnable={enableAlerts} onDisable={disableAlerts} />
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {!items.length && <p style={{ padding: 16, fontSize: 13, color: '#9a9a9a' }}>Nothing yet.</p>}
              {items.map(n => (
                <button key={n.id} onClick={() => go(n)}
                  style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', borderBottom: '1px solid #f4f4f4', background: n.read_at ? '#fff' : 'rgba(201,162,39,0.08)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 16 }}>{ICONS[n.type] || '🔔'}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: n.read_at ? 500 : 700, color: '#1a2744' }}>{n.title}</span>
                    {n.body && <span style={{ display: 'block', fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</span>}
                    <span style={{ fontSize: 11, color: '#9a9a9a' }}>{formatWhen(n.created_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pop-ups */}
      <div style={{ position: 'fixed', top: 70, right: 16, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 10, width: 340, maxWidth: 'calc(100vw - 32px)' }}>
        {toasts.map(n => (
          <div key={n.id} onClick={() => go(n)} role="alert"
            style={{ background: '#1a2744', color: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', cursor: 'pointer', borderLeft: '4px solid #c9a227', display: 'flex', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{ICONS[n.type] || '🔔'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{n.title}</div>
              {n.body && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.body}</div>}
              <div style={{ fontSize: 11, color: '#c9a227', marginTop: 4, fontWeight: 700 }}>Open →</div>
            </div>
            <button onClick={e => { e.stopPropagation(); setToasts(t => t.filter(x => x.id !== n.id)); }}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 14, alignSelf: 'flex-start' }}>✕</button>
          </div>
        ))}
      </div>
    </>
  );
}

function AlertsSetting({ state, onEnable, onDisable }) {
  const box = { padding: '10px 16px', borderBottom: '1px solid #eee', fontSize: 12, background: '#f8f7f2', color: '#555' };
  if (isIos && !isStandalone) {
    return <div style={box}>📱 <strong>iPhone alerts:</strong> tap Share → <em>Add to Home Screen</em>, open Premier Pawn Admin from your home screen, then turn on alerts here.</div>;
  }
  if (state === 'unsupported') return <div style={box}>This browser can't show alerts. You'll still get emails and pop-ups here.</div>;
  if (state === 'denied') return <div style={box}>Alerts are blocked for this site. Allow notifications in your browser settings to turn them on.</div>;
  if (state === 'on') {
    return <div style={{ ...box, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>✅ Alerts are on for this device <button onClick={onDisable} style={{ background: 'none', border: 'none', color: '#9a9a9a', fontSize: 12, cursor: 'pointer' }}>Turn off</button></div>;
  }
  return (
    <div style={{ ...box, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span>Get alerts on this device, even when this tab is closed.</span>
      <button className="btn btn-primary btn-sm" onClick={onEnable}>Turn on</button>
    </div>
  );
}
